import { useCallback, useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { get } from './lib/api'
import { restoreNativePush } from './lib/nativePush'
import { me, type User } from './lib/auth'
import { getLocale, setLocale, t } from './lib/i18n'
import SignIn from './components/SignIn'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'
import Icon from './components/Icon'
import Header from './worx/Header'
import SideSwitch, { type Side } from './worx/SideSwitch'
import { RolesContext } from './worx/roles'
import { saveLanguage, worx } from './worx/client'
import UpdateRequired from './worx/UpdateRequired'
import DesktopNav, { type Browse } from './worx/DesktopNav'
import { useDesktop } from './worx/desktop'
import { readNativeBuild } from './worx/buildInfo'
import { needsUpdate } from './lib/nativeUpdate'
import { firstName, greeting, tw } from './worx/strings'
import Welcome from './worx/Welcome'
import Celebration from './worx/Celebration'
import { listenForTaps } from './worx/feel'
import Worx from './worx/Worx'
import { listenForNativeHome, NATIVE_HOME } from './worx/nativeNavigation'
import WorxAdmin from './worx/WorxAdmin'
import { Detail, Feed } from './worx/Jobs'
import { ServiceDetailView, ServicesFeed } from './worx/Services'
import { sv } from './worx/servicesStrings'

export interface Health {
  /** Lowest native build allowed to run; 0 blocks nobody. */
  minNativeBuild?: number
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
  /** The owner account is unclaimed, so sign-up offers the setup token. */
  setupOpen?: boolean
}

type Tab = 'home' | 'admin' | 'splash'

/**
 * ── What a signed-out visitor sees ──────────────────────────────────────────
 *
 * The opening screen, and — if they tap past it — the real job feed. Both are
 * public, because the jobs endpoint is: somebody deciding whether this app is
 * worth installing should be able to see that there is work in it, and a
 * passkey prompt in front of a job board asks for commitment before value.
 * Applying is where the session becomes necessary, and that is where it is
 * asked for.
 */
/** True once this tab has already settled which language the account reads in. */
function langAdopted(): boolean {
  try { return Boolean(sessionStorage.getItem('worx.langAdopted')) } catch { return false }
}

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('home')
  const [intent, setIntent] = useState<Side | null>(null)
  const [signingIn, setSigningIn] = useState(false)
  // Tapped Register on the opening screen: the account form first, the role afterwards.
  const [registering, setRegistering] = useState(false)
  const [previewJob, setPreviewJob] = useState<string | null>(null)
  const [previewService, setPreviewService] = useState<string | null>(null)
  /*
   * A signed-out worker asked for the account form. Until then it stays shut.
   *
   * The form used to sit open under the feed. On a board with nothing in it
   * that was the whole screen, and App Review read it as a sign-up wall in
   * front of the listings (5.1.1(v), October 2026). Browsing needs nothing;
   * the form is one tap away for whoever wants to apply.
   */
  const [joining, setJoining] = useState(false)
  /*
   * Which side of the app is showing, owned HERE rather than inside the home
   * screen.
   *
   * It lived in Worx, so the switch vanished the moment you opened the admin
   * console — leaving an administrator on a screen with no way back to either
   * of the two views the app is actually for. The state and the control both
   * belong above the thing they switch.
   */
  const [side, setSide] = useState<Side>(() => (localStorage.getItem('worx.side') as Side) ?? 'worker')
  /*
   * The roles this account holds: null until the server answers, [] when the
   * account has not picked one yet. ONE is normal and means no switch bar; the
   * switch exists only for a hybrid account that added a second on purpose.
   */
  const [roles, setRoles] = useState<Side[] | null>(null)
  // This native build has been retired by the owner (see lib/nativeUpdate.ts). Fails open.
  const [retired, setRetired] = useState(false)
  // Anyone can look for a contractor, whatever their role: a browser over the current screen.
  const [browsing, setBrowsing] = useState<Browse>(null)
  const [browseOpen, setBrowseOpen] = useState<string | null>(null)
  const desktop = useDesktop()

  useEffect(() => listenForNativeHome(() => {
    setTab('home')
    window.dispatchEvent(new Event(NATIVE_HOME))
  }), [])

  const chooseSide = useCallback((next: Side) => {
    setSide(next)
    localStorage.setItem('worx.side', next)
    // Switching side is a request to SEE that side, so it also leaves admin.
    setTab('home')
  }, [])

  /*
   * The door somebody chose is the side they land on.
   *
   * A signed-out visitor picks a door on the splash (`intent`) and may browse
   * behind it; the moment they have an account that choice has to become their
   * side, or they are dropped on whatever the device last remembered — the
   * worker feed, for a plumber who tapped "I Need a Pro". Cleared afterwards so
   * a later sign-in from the splash is not hijacked by a stale choice.
   */
  useEffect(() => {
    if (!user) { setRoles(null); setRegistering(false); return }
    let live = true
    void (async () => {
      try {
        const asked = (await worx.roles()).roles
        // Anything but a list means the server did not really answer.
        if (!Array.isArray(asked)) throw new Error('roles')
        let held: Side[] = asked
        const brandNew = held.length === 0
        // The door chosen before signing up becomes the account's role, once.
        if (held.length === 0 && intent) {
          const added = (await worx.addRole(intent)).roles
          held = Array.isArray(added) ? added : [intent]
        }
        if (!live) return
        const mine = getLocale()
        if (brandNew) {
          // A brand-new account: the language they are reading in now is the one they picked.
          await saveLanguage(mine)
          // `user` still carries the old language until the next refresh; do not adopt it back.
          try { sessionStorage.setItem('worx.langAdopted', '1') } catch { /* best effort */ }
        } else if (user.locale !== mine && (user.locale === 'en' || user.locale === 'es-PR') && !langAdopted()) {
          // Signing in on another device: the account's language wins, so everything they see is in it.
          // Once per tab session, so a device that cannot hold the setting can never loop.
          try { sessionStorage.setItem('worx.langAdopted', '1') } catch { /* private mode: the guard is best effort */ }
          setLocale(user.locale)
          window.location.reload()
          return
        }
        setRoles(held)
        if (held.length > 0) {
          const next = intent && held.includes(intent) ? intent : held.includes(side) ? side : held[0]!
          chooseSide(next)
        }
        if (intent) { setIntent(null); setJoining(false); setSigningIn(false) }
      } catch {
        // The server could not say: carry on as the side this device last used
        // rather than stranding a signed-in person on a picker.
        if (live) setRoles([intent ?? side])
      }
    })()
    return () => { live = false }
    // `side` is read once for the starting choice; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, intent, chooseSide])

  const addRole = useCallback(async (role: Side) => {
    const next = (await worx.addRole(role)).roles
    setRoles(next)
    chooseSide(role)
  }, [chooseSide])

  const refresh = useCallback(async () => {
    /*
     * Health again, not just the session: `setupOpen` changes the moment the
     * owner registers, and the answer from boot would keep offering the setup
     * token to everyone who signs up in the same tab afterwards.
     */
    const [u, h] = await Promise.all([me(), get<Health>('/api/health').catch(() => null)])
    setUser(u)
    if (h) setHealth(h)
  }, [])

  useEffect(() => {
    const min = health?.minNativeBuild
    if (!min) return
    let live = true
    void readNativeBuild().then((native) => { if (live && native && needsUpdate(native.build, min)) setRetired(true) }).catch(() => undefined)
    return () => { live = false }
  }, [health?.minNativeBuild])

  // Every button ticks. Once, here, rather than in every handler.
  useEffect(() => listenForTaps(), [])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  useEffect(() => {
    const restore = () => {
      if (document.visibilityState === 'visible') {
        // Registration failures stay retryable from Profile; never log APNs tokens.
        void restoreNativePush(user?.id ?? null).catch(() => undefined)
      }
    }
    if (ready) restore()
    window.addEventListener('focus', restore)
    document.addEventListener('visibilitychange', restore)
    return () => {
      window.removeEventListener('focus', restore)
      document.removeEventListener('visibilitychange', restore)
    }
  }, [ready, user?.id])

  if (!ready) return <div className="boot">{t('app.loading')}</div>
  if (retired) return <UpdateRequired />

  const showWelcome = !user && !intent && !signingIn && !registering
  /*
   * The opening screen owns the whole screen.
   *
   * Signed out it is the first thing anyone sees; signed in it is reached by
   * "Splash" beside the switch. Either way the app bar comes off: a lockup,
   * a greeting, a bell and two flags stacked above an introduction is the app
   * talking over itself, and the mockup has none of it. The screen carries its
   * own language control instead.
   */
  // Signed in but no role yet (the choice before sign-up was lost): ask once.
  const pickingRole = Boolean(user) && roles !== null && roles.length === 0
  const splash = showWelcome || pickingRole

  return (
    <ErrorBoundary>
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <Celebration />
      <EnvBanner health={health} />

      {!splash && (
        <Header
          appName={health?.app ?? 'Vieques Worx'}
          greeting={user ? { hello: greeting(), name: firstName(user.displayName) } : undefined}
          canAdmin={Boolean(user?.capabilities.includes('admin.access'))}
          adminOpen={tab === 'admin'}
          onToggleAdmin={() => setTab(tab === 'admin' ? 'home' : 'admin')}
          badge={desktop && health?.environment === 'beta' ? tw('w.nav.betaPreview') : undefined}
          nav={desktop && user && tab === 'home' && roles && roles.length > 0
            ? <DesktopNav side={side} browsing={browsing} onBrowse={(next) => { setBrowseOpen(null); setBrowsing(next) }} />
            : undefined}
        />
      )}

      <main className={splash ? 'w-main-splash' : 'w-main-app'}>
        {/* Above everything the two sides share, and outside the screens it
            switches between, so it is reachable from the admin console too. */}
        {user && roles && roles.length > 1 && (
          <SideSwitch side={side} roles={roles} onChoose={chooseSide} />
        )}

        {/*
          The opening screen, for somebody who already has an account.

          It is the nicest thing in the app and a signed-in person could never
          see it again — so "Splash" beside the switch brings it back, and the
          two role cards do what they have always done: choose a side and take
          you to it.
        */}
        {pickingRole && (
          <Welcome
            signedIn
            onChoose={(next) => { void addRole(next) }}
          />
        )}
        {showWelcome && (
          <Welcome
            onChoose={setIntent}
            onSignIn={() => setSigningIn(true)}
            onRegister={() => setRegistering(true)}
          />
        )}

        {!user && registering && !signingIn && !intent && (
          <div className="w-stack">
            <button className="link w-back" onClick={() => setRegistering(false)}>
              <Icon name="back" size={16} /> {tw('w.nav.back')}
            </button>
            <SignIn onSignedIn={refresh} startWith="up" setupOpen={health?.setupOpen} />
          </div>
        )}

        {!user && signingIn && (
          <div className="w-stack">
            <button className="link w-back" onClick={() => setSigningIn(false)}>
              <Icon name="back" size={16} /> {tw('w.nav.back')}
            </button>
            <SignIn onSignedIn={refresh} setupOpen={health?.setupOpen} />
          </div>
        )}

        {/*
          Chose a side but has no account yet.
          The worker path shows the feed FIRST and registration second: there is
          something to look at before there is anything to fill in, which is the
          difference between a job board and a sign-up wall. The employer path
          has nothing to show until a business exists, so it goes straight to
          the passkey.
        */}
        {!user && intent && !signingIn && (
          /*
            A stack, not a fragment. `.card` carries a bottom margin and no top
            one, so the sign-in panel sat flush against the last line of
            whatever was above it — the employer's description, with no gap at
            all. A gap on the container fixes it once for every combination
            rather than per screen.
          */
          <div className="w-stack">
            {/* Only one back link on screen at a time: the job detail has its
                own, and two stacked arrows pointing at different places is a
                navigation the reader has to decode. */}
            {!previewJob && !previewService && (
              <button className="link w-back" onClick={() => { setIntent(null); setJoining(false) }}>
                <Icon name="back" size={16} /> {tw('w.nav.back')}
              </button>
            )}
            {intent === 'worker' && !previewJob && <Feed onOpen={setPreviewJob} />}
            {intent === 'worker' && previewJob && (
              <Detail
                id={previewJob}
                signedIn={false}
                onBack={() => setPreviewJob(null)}
                onApplied={() => {}}
              />
            )}
            {intent === 'worker' && !joining && (
              <div className="card w-browse">
                <p>{tw('w.browse.free')}</p>
                <button onClick={() => setJoining(true)}>{tw('w.browse.join')}</button>
              </div>
            )}
            {/* Choosing a side is the new-person path — "Sign in" is its own
                button on the opening screen — so this opens on Create account.
                A business has nothing to browse until it exists, so the
                employer path still opens straight on the form. */}
            {/* The third door browses the same way: the list first, the account
                form only when someone wants to contact a pro. */}
            {intent === 'pro' && !previewService && <ServicesFeed onOpen={setPreviewService} />}
            {intent === 'pro' && previewService && (
              <ServiceDetailView
                id={previewService}
                signedIn={false}
                onBack={() => setPreviewService(null)}
                onNeedAccount={() => { setPreviewService(null); setJoining(true) }}
              />
            )}
            {intent === 'pro' && !joining && !previewService && (
              <div className="card w-browse">
                <p>{sv('sv.browse.free')}</p>
                <button onClick={() => setJoining(true)}>{sv('sv.browse.join')}</button>
              </div>
            )}
            {(intent === 'employer' || joining) && (
              <SignIn onSignedIn={refresh} startWith="up" setupOpen={health?.setupOpen} />
            )}
          </div>
        )}

        {user && tab === 'home' && roles && roles.length > 0 && (
          <RolesContext.Provider value={{ roles, add: addRole, findPro: () => { setBrowseOpen(null); setBrowsing('pros') } }}>
          {browsing && (
            <div className="w-stack w-browse-over">
              {!desktop && (
                <button className="link w-back" onClick={() => { setBrowsing(null); setBrowseOpen(null) }}>
                  <Icon name="back" size={16} /> {tw('w.pro.back')}
                </button>
              )}
              {browsing === 'pros' && !browseOpen && <ServicesFeed signedIn onOpen={setBrowseOpen} />}
              {browsing === 'pros' && browseOpen && <ServiceDetailView key={browseOpen} id={browseOpen} signedIn onBack={() => setBrowseOpen(null)} />}
              {browsing === 'jobs' && !browseOpen && <Feed onOpen={setBrowseOpen} />}
              {browsing === 'jobs' && browseOpen && <Detail key={browseOpen} id={browseOpen} signedIn onBack={() => setBrowseOpen(null)} onApplied={() => undefined} />}
            </div>
          )}
          {/* Kept mounted, only hidden, so coming back lands on the tab you left. */}
          <div hidden={browsing !== null}>
          <Worx
            user={user}
            side={side}
            environment={health?.environment}
            onSignedOut={refresh}
            onSwitchToWorker={() => chooseSide('worker')}
          />
          </div>
          </RolesContext.Provider>
        )}
        {user && tab === 'admin' && (
          <>
            <Admin />
            {/* Beside core's console, never inside it — see WorxAdmin.tsx. */}
            <WorxAdmin capabilities={user.capabilities} />
          </>
        )}
      </main>

      {desktop && user && !splash && (
        <footer className="w-site-footer">
          <img src="/mark.webp" alt="" width={44} height={31} />
          <strong>Vieques Worx</strong>
          <span>{tw('w.footer.tagline')}</span>
        </footer>
      )}

      {/* Capability, not environment: the phone that needs diagnosing is always
          a real one in production. */}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}

      {/* Below the fold, not in the way. For an app that is not on a store yet
          — and not at all on the opening screen, which is a full-screen
          takeover and cannot have a card hanging off the bottom of it. */}
      {/* "Add to home screen" is for a browser. Inside the native app it is nonsense and it covers the tab bar. */}
      {!splash && !Capacitor.isNativePlatform() && <InstallPrompt />}

      {/* The build stamp, on the same rule as the debug panel above: a
          capability, not an environment.

          It used to render for everybody. That was harmless while the app was
          a URL we passed around, and it stopped being harmless the moment the
          app had store screenshots — "0.1.0 · 82d0922-dirty · production" sat
          at the bottom of one of them, which is both a developer artifact in a
          shop window and a commit hash printed to every visitor. The `-dirty`
          suffix additionally tells the world that what is deployed was built
          from an uncommitted tree.

          Whoever needs it is diagnosing a real phone in production, and they
          are the same person the debug panel is for. */}
      {user?.capabilities.includes('errors.read') && (
        <footer className="muted small">
          {health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}
        </footer>
      )}
    </ErrorBoundary>
  )
}
